/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Cluster3xNodeName } from './cluster3x';
import { Integer, PaginatedDataResponse, SignedInteger, TimestampInMs, UuidV4 } from './common';

export type QueryRunId3x = UuidV4;

export type SqlQueryExpression3x = string;
export type SchemaName3x = string;

// Create query body
export interface CreateQuery3xRequestBody {
  pageSize: Integer;
  queryText: SqlQueryExpression3x;
}

// Create query response
export type CursorId3x = UuidV4;
export enum QueryType3x {
  QUERY = 'QUERY',
  DML = 'DML',
  DDL = 'DDL',
  EXPLAIN = 'EXPLAIN',
  KILL = 'KILL',
  TX_CONTROL = 'TX_CONTROL',
  SHOW = 'SHOW',
  COPY = 'COPY',
}
export interface CursorInfo3x {
  id: CursorId3x;
  statement: string;
  queryType: QueryType3x;
  success: boolean;
}
export type CreateQuery3xResponseBody = CursorInfo3x[];

// Next page response
export interface QueryField3x {
  fieldName: string;
  fieldTypeName: string;
  schemaName: SchemaName3x | null;
  typeName: string | null;
}

export type Query3xExecutionResultOk = {
  columns: QueryField3x[];
  rows: (number | string)[][];
  hasMore: boolean;
  errorMessage: null;
};

export type Query3xExecutionResultError = {
  columns: null;
  rows: null;
  hasMore: null;
  errorMessage: string;
};

export type Query3xExecutionResult = Query3xExecutionResultOk | Query3xExecutionResultError;

// Running queries
export enum Query3xStatus {
  RUNNING = 'RUNNING',
  FAILED = 'FAILED',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
  UNKNOWN = 'UNKNOWN',
}
export const Query3xStatuses = [null, ...Object.values(Query3xStatus)] as const;
export type Query3xStatusType = (typeof Query3xStatuses)[number];

export type RunningQueryId3x = UuidV4;

export interface RunningQuery3x {
  id: RunningQueryId3x;
  initiatorNode: Cluster3xNodeName;
  type: QueryType3x | null;
  status: Query3xStatus;
  startedAt: TimestampInMs;
  duration: SignedInteger | null;
  transactionId: UuidV4 | null;
  parentId: UuidV4 | null;
  statementNum: SignedInteger | null;
  schema: SchemaName3x;
  sql: SqlQueryExpression3x;
  errorMessage: string | null;
}

export type RunningQueries3xPaged = PaginatedDataResponse<RunningQuery3x>;

export interface RunningQueries3xQueryParams {
  page?: `${SignedInteger}`;
  size?: `${SignedInteger}`;
  paginationSessionId?: UuidV4;
  startedAt?: `${TimestampInMs}` | [`${TimestampInMs}`, `${TimestampInMs}`];
  status?: Query3xStatus;
  type?: QueryType3x;
  duration?: `${SignedInteger}`;
  schema?: string;
  sql?: string;
}

export type RunningQueries3xFilterQueryParams = Exclude<
  RunningQueries3xQueryParams,
  'paginationSessionId' | 'page' | 'size'
>;

export enum SystemView3x {
  'COMPUTE_TASKS' = 'COMPUTE_TASKS',
  'GLOBAL_PARTITION_STATES' = 'GLOBAL_PARTITION_STATES',
  'INDEXES' = 'INDEXES',
  'INDEX_COLUMNS' = 'INDEX_COLUMNS',
  'LICENSES' = 'LICENSES',
  'LOCKS' = 'LOCKS',
  'LOCAL_PARTITION_STATES' = 'LOCAL_PARTITION_STATES',
  'SCHEMAS' = 'SCHEMAS',
  'SEQUENCES' = 'SEQUENCES',
  'SQL_QUERIES' = 'SQL_QUERIES',
  'SYSTEM_VIEWS' = 'SYSTEM_VIEWS',
  'SYSTEM_VIEW_COLUMNS' = 'SYSTEM_VIEW_COLUMNS',
  'TABLES' = 'TABLES',
  'TABLE_COLUMNS' = 'TABLE_COLUMNS',
  'TRANSACTIONS' = 'TRANSACTIONS',
  'ZONES' = 'ZONES',
  'ZONE_STORAGE_PROFILES' = 'ZONE_STORAGE_PROFILES',
}
